import Marquee from "react-fast-marquee";
import { Reveal } from "@/components/motion";
import { CornerBird } from "@/components/brand";
import { GoldButton, WhatsAppButton } from "@/components/cta";

/** Standard content section on cream background. */
export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`relative py-20 md:py-28 ${className}`}>
    <div className="mx-auto max-w-[1400px] px-6 md:px-12">{children}</div>
  </section>
);

/** Small eyebrow label. */
export const Eyebrow = ({ children, dark = false }) => (
  <span className={`font-sans text-xs font-600 uppercase tracking-[0.24em] ${dark ? "text-gold" : "text-gold"}`}>
    {children}
  </span>
);

/** Dark navy hero used on inner pages. */
export const PageHero = ({ eyebrow, title, intro, breadcrumb }) => (
  <section className="relative overflow-hidden bg-navy pt-32 pb-20 md:pt-40 md:pb-28">
    <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-slate/30" />
    <div className="pointer-events-none absolute right-10 top-24 h-24 w-24 rounded-full bg-gold/80" />
    <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
      {breadcrumb && (
        <Reveal className="mb-6 font-sans text-xs uppercase tracking-[0.2em] text-white/50">{breadcrumb}</Reveal>
      )}
      {eyebrow && (
        <Reveal>
          <Eyebrow dark>{eyebrow}</Eyebrow>
        </Reveal>
      )}
      <Reveal delay={0.05}>
        <h1 className="mt-4 max-w-4xl font-serif text-4xl font-600 leading-[1.05] tracking-tight text-white md:text-6xl">
          {title}
        </h1>
      </Reveal>
      {intro && (
        <Reveal delay={0.12}>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/80">{intro}</p>
        </Reveal>
      )}
    </div>
  </section>
);

/** Section heading block for cream sections (serif navy). */
export const SectionHead = ({ eyebrow, title, sub, bird = false }) => (
  <div className="relative mb-14 max-w-3xl">
    {bird && <CornerBird className="!right-0 !-top-6" />}
    {eyebrow && (
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <h2 className="mt-3 font-serif text-4xl font-600 leading-[1.08] tracking-tight text-navy md:text-5xl">
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.1}>
        <p className="mt-4 text-lg leading-relaxed text-mutedink">{sub}</p>
      </Reveal>
    )}
  </div>
);

/** Slow editorial marquee. */
export const EditorialMarquee = () => (
  <div className="border-y border-navy/10 bg-cream py-8">
    <Marquee speed={26} gradient={false} autoFill>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="mx-8 flex items-center gap-8 font-serif text-4xl italic text-navy/25 md:text-6xl">
          Sankofa Launch Studio
          <img src="/assets/sankofa-bird.png" alt="" aria-hidden="true" className="h-8 w-8 opacity-40 md:h-12 md:w-12" />
        </span>
      ))}
    </Marquee>
  </div>
);

/** Final dark navy CTA band, reused across pages. */
export const CtaBand = ({ title = "Let's build yours", text = "Book your briefing this week and we'll get your project into the next sprint.", waText }) => (
  <section className="relative overflow-hidden bg-navy-dark py-20 md:py-28">
    <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-slate/20" />
    <div className="pointer-events-none absolute right-12 top-10 h-20 w-20 rounded-full bg-gold/70" />
    <div className="relative mx-auto max-w-[1400px] px-6 text-center md:px-12">
      <Reveal>
        <h2 className="mx-auto max-w-3xl font-serif text-4xl font-600 leading-tight text-white md:text-5xl">{title}</h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mx-auto mt-5 max-w-xl text-lg text-white/75">{text}</p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <GoldButton size="lg" testid="cta-band-book" />
          <WhatsAppButton size="lg" text={waText} testid="cta-band-whatsapp" />
        </div>
      </Reveal>
    </div>
  </section>
);
